import { useCallback, useMemo } from 'react';
import type {
  WarehouseGateway,
  WarehouseOverview,
  WarehouseReplenishmentRecord,
} from '@forq/warehouse-data';
import type { ResolvedPeriod } from './period';
import { ReportActions, type SectionReport } from './report';
import { CustomizableMetrics } from '@forq/ui';
import { euro0, Kpi, KpiGrid, num, Panel, SectionShell, useAsyncData } from './shared';

function unitLabel(unit: string): string {
  const labels: Record<string, string> = { piece: 'Stk.', hour: 'Std.', kilometer: 'km', 'flat-rate': 'Pausch.', meter: 'm', kilogram: 'kg', liter: 'l' };
  return labels[unit] ?? unit;
}

/** Lager-Auswertung. Momentaufnahme: Bestände sind immer der aktuelle Stand (der Zeitraum wirkt hier nicht). */
export function WarehouseSection({ tenantId, gateway, period, tenantName }: {
  readonly tenantId: string;
  readonly gateway: WarehouseGateway;
  readonly period: ResolvedPeriod;
  readonly tenantName: string;
}) {
  const load = useCallback(async (): Promise<{ overview: WarehouseOverview; replenishment: readonly WarehouseReplenishmentRecord[]; stockValue: number }> => {
    const [overview, replenishment, stock, articles] = await Promise.all([
      gateway.getOverview(tenantId),
      gateway.listReplenishment(tenantId),
      gateway.searchStock({ tenantId, query: '', filter: 'all', locationId: 'all', pageSize: 1000 }),
      gateway.searchArticleOptions(tenantId),
    ]);
    // Lagerbestandswert (EK) = Σ Menge × Einkaufspreis; Menge (Bestand) und EK (Artikelstamm) joinen.
    const ekByItem = new Map(articles.map((article) => [article.id, article.preferredPurchasePrice] as const));
    const stockValue = stock.items.reduce((sum, row) => sum + row.onHand * (ekByItem.get(row.itemId) ?? 0), 0);
    return { overview, replenishment, stockValue };
  }, [gateway, tenantId]);
  const { data, loading, error, reload } = useAsyncData(load);

  const report: SectionReport | null = useMemo(() => {
    if (!data) return null;
    return {
      title: 'Lager',
      kpis: [
        { label: 'Lagerbestandswert (EK)', value: euro0(data.stockValue) },
        { label: 'Artikel mit Bestand', value: num(data.overview.stockedItems) },
        { label: 'Unter Mindestbestand', value: num(data.overview.lowStockItems) },
        { label: 'Negative Bestände', value: num(data.overview.negativeStockItems) },
        { label: 'Beschaffungsbedarf', value: num(data.overview.openReplenishmentItems) },
        { label: 'Aktive Lagerplätze', value: num(data.overview.activeLocations) },
      ],
      blocks: [
        { heading: 'Beschaffungsbedarf', columns: ['Artikel', 'Lieferant', 'Vorschlag', 'Priorität'], rows: data.replenishment.map((item) => [`${item.itemName} (${item.itemNumber})`, item.supplierName || 'Nicht zugeordnet', `${num(item.suggestedQuantity)} ${unitLabel(item.unit)}`, item.urgency === 'critical' ? 'Kritisch' : 'Niedrig']) },
      ],
    };
  }, [data]);

  return (
    <SectionShell loading={loading} error={error} onReload={reload}>
      {data && report ? (
        <div className="auswertung__stack">
          <div className="auswertung__sectionhead">
            <span className="sales-eyebrow">Lager · Momentaufnahme</span>
            <ReportActions report={report} period={period} tenantName={tenantName} />
          </div>
          <KpiGrid>
            <CustomizableMetrics
              pageKey="auswertung-lager"
              tiles={[
                { key: 'stockvalue', node: <Kpi label="Lagerbestandswert (EK)" value={euro0(data.stockValue)} hint="Menge × Einkaufspreis" /> },
                { key: 'stocked', node: <Kpi label="Artikel mit Bestand" value={num(data.overview.stockedItems)} hint={`${num(data.overview.activeLocations)} aktive Lagerplätze`} /> },
                { key: 'lowstock', node: <Kpi label="Unter Mindestbestand" value={num(data.overview.lowStockItems)} hint="nachbeschaffen" /> },
                { key: 'negative', node: <Kpi label="Negative Bestände" value={num(data.overview.negativeStockItems)} hint="prüfen / korrigieren" /> },
                { key: 'replenish', node: <Kpi label="Beschaffungsbedarf" value={num(data.overview.openReplenishmentItems)} hint="offene Positionen" /> },
                { key: 'movements', node: <Kpi label="Bewegungen heute" value={num(data.overview.movementsToday)} hint="Zu-/Abgänge/Umlagerungen" /> },
              ]}
            />
          </KpiGrid>

          <Panel title="Dringendster Beschaffungsbedarf">
            {data.replenishment.length === 0 ? (
              <p className="sales-controlling__empty">Aktuell besteht kein Beschaffungsbedarf.</p>
            ) : (
              <table className="sales-controlling__table">
                <thead><tr><th>Artikel</th><th>Lieferant</th><th className="is-num">Vorschlag</th><th>Priorität</th></tr></thead>
                <tbody>
                  {data.replenishment.slice(0, 8).map((item) => (
                    <tr key={item.itemId}>
                      <td>{item.itemName}<small style={{ display: 'block', color: 'var(--forq-text-muted)' }}>{item.itemNumber}</small></td>
                      <td>{item.supplierName || 'Nicht zugeordnet'}</td>
                      <td className="is-num">{num(item.suggestedQuantity)} {unitLabel(item.unit)}</td>
                      <td>{item.urgency === 'critical' ? 'Kritisch' : 'Niedrig'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </Panel>
        </div>
      ) : null}
    </SectionShell>
  );
}
